import SaveBar from '@/components/forms/SaveBar'
import FieldInfo from '@/components/form/FieldInfo'
import { getFieldErrorMessages } from '@/components/form/InlineFieldErrors'
import { AdminSectionHeader } from '@/components/patterns/admin-page'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useId, useState } from 'react'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { useUpdateOrganizationTaxSettingsMutation } from '@/lib/services/organization-tax-settings/organization-tax-settings.query'
import {
  organizationTaxSettingsSchema,
  taxSettingsFormValues,
  taxSettingsPayload,
} from '@/lib/services/organization-tax-settings/organization-tax-settings.schema'
import { getErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import { TaxCollectionStates } from './TaxCollectionStates'
import { TaxPolicyFields } from './TaxPolicyFields'

export function TaxSettingsForm({ settings, organizationId, isReadOnly }) {
  const locations = settings.locations ?? []
  const [selectedLocationId, setSelectedLocationId] = useState(() => locations[0]?.id)
  const locationSelectId = useId()
  const selectedLocation =
    locations.find((location) => location.id === selectedLocationId) ?? locations[0]
  const updateMutation = useUpdateOrganizationTaxSettingsMutation()
  const form = useAppForm({
    defaultValues: taxSettingsFormValues(settings),
    validators: {
      onSubmit: organizationTaxSettingsSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return
        try {
          const saved = await updateMutation.mutateAsync(taxSettingsPayload(value))
          formApi.reset(taxSettingsFormValues(saved))
          toast.success('Tax settings updated')
        } catch (error) {
          const current = error?.response?.data?.current
          if (error?.response?.status === 409 && current) {
            formApi.reset(taxSettingsFormValues(current))
            toast.error('These tax settings changed in another session', {
              description: 'We loaded the latest settings. Review them before saving again.',
            })
            return
          }
          toast.error('Failed to update tax settings', { description: getErrorMessage(error) })
          if (error?.response?.data?.errors) return { fields: error.response.data.errors }
        }
      },
    },
  })
  return (
    <form.AppForm>
      <form.Form className="space-y-5">
        <form.Subscribe
          selector={(state) => ({ values: state.values, fieldMeta: state.fieldMeta })}
        >
          {({ values, fieldMeta }) => {
            const locationIndex = values.location_overrides.findIndex(
              (override) => override.location_id === selectedLocation?.id
            )
            const customize = values.customize_by_location && locationIndex >= 0
            const policy = customize ? values.location_overrides[locationIndex] : values
            const effectivePolicy = policy.calculation_mode === 'inherit' ? values : policy
            const fullyAutomatic =
              effectivePolicy.calculation_mode === 'automatic' &&
              effectivePolicy.delivery_tax_mode === 'automatic'
            const policyGuidance = !fullyAutomatic ? (
              <CardFooter>
                <Typography variant="supporting">
                  Changes apply only to new orders. You’re responsible for confirming these settings
                  are right for your shop. Check with a local tax professional for guidance.
                </Typography>
              </CardFooter>
            ) : null
            const visibleRatePath =
              policy.calculation_mode === 'fixed'
                ? customize
                  ? `location_overrides[${locationIndex}].fixed_tax_rate`
                  : 'fixed_tax_rate'
                : null
            const hiddenRateErrors = [
              { path: 'fixed_tax_rate', label: 'Same for all locations' },
              ...values.location_overrides.map((override, index) => ({
                path: `location_overrides[${index}].fixed_tax_rate`,
                label:
                  locations.find((location) => location.id === override.location_id)?.name ??
                  'Location tax rate',
              })),
            ]
              .filter(({ path }) => path !== visibleRatePath)
              .flatMap(({ path, label }) =>
                getFieldErrorMessages(fieldMeta, path).map((message) => `${label}: ${message}`)
              )
            const policyFields = (
              <TaxPolicyFields
                key={customize ? selectedLocation.id : 'all'}
                form={form}
                organizationId={organizationId}
                location={customize ? selectedLocation : undefined}
                prefix={customize ? `location_overrides[${locationIndex}].` : ''}
                inheritedPolicy={values}
                deliveryModes={settings.delivery_tax_modes}
                isReadOnly={isReadOnly}
              />
            )

            return (
              <>
                {hiddenRateErrors.length > 0 ? (
                  <Alert variant="destructive">
                    <AlertTitle>Review your tax rates before saving</AlertTitle>
                    <AlertDescription>{hiddenRateErrors.join(' ')}</AlertDescription>
                  </Alert>
                ) : null}
                <Card>
                  <CardContent className="space-y-6">
                    {locations.length > 1 || settings.customize_by_location ? (
                      <form.AppField name="customize_by_location">
                        {(field) => (
                          <field.RadioButtonGroup
                            label="Manage taxes"
                            options={[
                              { id: 'false', name: 'Same for all locations' },
                              { id: 'true', name: 'Customize by location' },
                            ]}
                            parseValue={(value) => value === 'true'}
                            appearance="segmented"
                            orientation="responsive"
                            classNames={{ container: '@md/segmented-control:w-fit' }}
                            disabled={isReadOnly}
                          />
                        )}
                      </form.AppField>
                    ) : null}
                    {customize ? (
                      <div className="max-w-sm space-y-2">
                        <Label htmlFor={locationSelectId}>Location</Label>
                        <Select
                          value={String(selectedLocation.id)}
                          onValueChange={(id) => setSelectedLocationId(Number(id))}
                        >
                          <SelectTrigger id={locationSelectId} className="w-full">
                            <SelectValue>
                              {selectedLocation.name}
                              {selectedLocation.is_default ? ' (Primary)' : ''}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((location) => (
                              <SelectItem
                                key={location.id}
                                value={String(location.id)}
                                size="comfortable"
                              >
                                <span>
                                  {location.name}
                                  {location.is_default ? ' (Primary)' : ''}
                                  {location.address ? (
                                    <Typography as="span" variant="supporting" className="block">
                                      {location.address}
                                    </Typography>
                                  ) : null}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                    {!customize ? policyFields : null}
                  </CardContent>
                  {!customize ? policyGuidance : null}
                </Card>
                {customize ? (
                  <Card>
                    <CardHeader>
                      <AdminSectionHeader
                        title={`Tax settings for ${selectedLocation.name}`}
                        description={selectedLocation.address}
                      />
                    </CardHeader>
                    <CardContent>{policyFields}</CardContent>
                    {policyGuidance}
                  </Card>
                ) : null}
                {effectivePolicy.calculation_mode === 'automatic' ? (
                  <form.AppField name="additional_nexus_states">
                    {(field) => (
                      <TaxCollectionStates
                        value={field.state.value}
                        onChange={field.handleChange}
                        stateOptions={settings.state_options}
                        activeLocationStates={settings.active_location_states}
                        allLocations={customize}
                        isReadOnly={isReadOnly}
                        errors={field.state.meta.errors}
                      />
                    )}
                  </form.AppField>
                ) : null}
              </>
            )
          }}
        </form.Subscribe>
        <Card>
          <CardContent className="space-y-4">
            <Typography variant="sectionTitle" as="h2">
              Your responsibility
            </Typography>
            <Typography variant="description">{settings.responsibility}</Typography>
            <form.AppField name="responsibility_acknowledged">
              {(field) => (
                <div className="space-y-2">
                  <field.CheckboxInput
                    label="I agree to the sales tax terms above."
                    disabled={isReadOnly}
                  />
                  <FieldInfo />
                </div>
              )}
            </form.AppField>
          </CardContent>
        </Card>
        {!isReadOnly ? <SaveBar mutation={updateMutation} /> : null}
      </form.Form>
    </form.AppForm>
  )
}
